import React, { useMemo } from 'react';
import ItemsTable from './ItemsTable';
import LazyMount from './LazyMount';
import { groupBySupplier, sortItems, money, num } from '../reorderUtils';

/**
 * Above this many rows a block scrolls internally and virtualises.
 *
 * Set low on purpose. At 60 only 3 of 107 blocks virtualised, so the other 104
 * still mounted every row — 30,017 nodes — and switching between By Supplier
 * and Spreadsheet had to tear all of that down, which is what the lag was.
 * Small blocks still render whole so they grow on the page with no nested
 * scrollbar to fight.
 */
const VIRTUALIZE_ABOVE = 25;
const BLOCK_HEIGHT = 460;

/**
 * The reorder sheet grouped into one collapsible block per supplier.
 *
 * Buying happens per supplier, not per part, so the block — with its own item
 * count, shortfall count, value and export buttons — is the unit of work. The
 * worst suppliers sort to the top.
 */
const SupplierBlocks = ({
    items,
    sort,
    onSort,
    isCombined = false,
    monthCover = false,
    collapsed,
    onToggle,
    editable = false,
    busyPart = '',
    onEdit,
    onReset,
    fallbackBranchId = '',
    onExportSupplier,
    onOrderCsv,
    exportsBlocked = false,
    exportsBlockedReason = ''
}) => {
    // Grouping and sorting are O(n log n) over the whole sheet — without this
    // they ran again on every keystroke and every checkbox toggle.
    const groups = useMemo(
        () =>
            groupBySupplier(items).map((group) => ({
                ...group,
                items: sortItems(group.items, sort)
            })),
        [items, sort]
    );

    if (groups.length === 0) {
        return <div className="sr-empty">Nothing matches these filters.</div>;
    }

    return (
        <div className="sr-blocks">
            {groups.map((group) => {
                const isCollapsed = collapsed.has(group.supplier);
                return (
                    <div className="sr-block" key={group.supplier}>
                        <div className="sr-block-head">
                            <button
                                type="button"
                                className="sr-block-toggle"
                                onClick={() => onToggle(group.supplier)}
                                aria-expanded={!isCollapsed}
                            >
                                <span className={`sr-chevron ${isCollapsed ? 'collapsed' : ''}`}>▾</span>
                                {group.supplier}
                                {group.isInternational && (
                                    <span className="sr-tag" title="International supplier">
                                        intl
                                    </span>
                                )}
                            </button>

                            <div className="sr-block-meta">
                                <span>
                                    {group.items.length} item{group.items.length === 1 ? '' : 's'}
                                </span>
                                {group.needingCount > 0 && (
                                    <span className="sr-block-alert">
                                        {group.needingCount} need reorder
                                    </span>
                                )}
                                <span>Value {money(group.reorderValue)}</span>

                                {onExportSupplier && (
                                    <button
                                        type="button"
                                        className="sr-btn sr-btn-sm"
                                        title={
                                            exportsBlocked
                                                ? exportsBlockedReason
                                                : `Export ${group.supplier} to Excel`
                                        }
                                        disabled={exportsBlocked}
                                        onClick={() => onExportSupplier(group.supplier)}
                                    >
                                        Export
                                    </button>
                                )}
                                {onOrderCsv && (
                                    <button
                                        type="button"
                                        className="sr-btn sr-btn-sm"
                                        title={
                                            exportsBlocked
                                                ? exportsBlockedReason
                                                : `Purchase order CSV for ${group.supplier}`
                                        }
                                        disabled={exportsBlocked}
                                        onClick={() => onOrderCsv(group.supplier)}
                                    >
                                        Order CSV
                                    </button>
                                )}
                            </div>
                        </div>

                        {!isCollapsed && (
                            <LazyMount
                                placeholderHeight={Math.min(
                                    group.items.length * 32 + 40,
                                    BLOCK_HEIGHT
                                )}
                            >
                                <ItemsTable
                                    items={group.items}
                                    isCombined={isCombined}
                                    monthCover={monthCover}
                                    sort={sort}
                                    onSort={onSort}
                                    editable={editable}
                                    busyPart={busyPart}
                                    onEdit={onEdit}
                                    onReset={onReset}
                                    fallbackBranchId={fallbackBranchId}
                                    virtualize={group.items.length > VIRTUALIZE_ABOVE}
                                    height={BLOCK_HEIGHT}
                                />
                                {group.items.length > VIRTUALIZE_ABOVE && (
                                    <div className="sr-block-more">
                                        All {num(group.items.length)} rows — scroll inside the block
                                    </div>
                                )}
                            </LazyMount>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default SupplierBlocks;
